import { useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  LocationWizardSteps,
  LocationWizardStep,
  LOCATION_WIZARD_STEP_ORDER,
  LOCATION_WIZARD_STEP_LABELS
} from '../../../constants/LocationWizardConstants';
import { PAGE_LOCATIONS } from '../../../constants/UriConstants';
import tapTabLogo from '../../../assets/images/TapTabLargeLogoWhite.png';
import LocationBasicInfoStep, { BasicInfoData } from './LocationBasicInfoStep';
import LocationHoursStep, { HoursData } from './LocationHoursStep';
import LocationContactStep, { ContactData } from './LocationContactStep';
import { useBrandContext } from '../../../contexts/BrandContext';
import { createRestaurant } from '../../../api/restaurant';
import { assignRestaurantToBrand } from '../../../api/brand';

const DAY_MAP = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const initialBasicInfo: BasicInfoData = {
  restaurantName: '',
  streetAddress1: '',
  streetAddress2: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  email: ''
};

const initialHours: HoursData = {
  entries: [
    {
      days: [false, false, false, false, false, false, false],
      startTime: '',
      endTime: ''
    }
  ]
};

const initialContact: ContactData = {
  firstName: '',
  lastName: '',
  title: '',
  phone: '',
  email: ''
};

interface ProgressBarProps {
  currentStep: LocationWizardStep;
}

const ProgressBar = ({ currentStep }: ProgressBarProps) => {
  const currentIndex = LOCATION_WIZARD_STEP_ORDER.indexOf(currentStep);
  const totalSteps = LOCATION_WIZARD_STEP_ORDER.length;
  const progressPercent = ((currentIndex + 1) / totalSteps) * 100;

  return (
    <div style={{ width: '100%', maxWidth: '800px', margin: '0 auto', padding: '0 24px 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
        {LOCATION_WIZARD_STEP_ORDER.map((step, index) => (
          <span
            key={step}
            style={{
              fontSize: '0.875rem',
              fontWeight: index === currentIndex ? 700 : 400,
              color: index <= currentIndex ? 'white' : 'rgba(255,255,255,0.5)',
              textAlign: 'center',
              flex: 1
            }}
          >
            {LOCATION_WIZARD_STEP_LABELS[step]}
          </span>
        ))}
      </div>
      <div
        style={{ height: '10px', backgroundColor: 'rgba(255,255,255,0.3)', borderRadius: '5px', overflow: 'hidden' }}
      >
        <div
          style={{
            height: '100%',
            width: `${progressPercent}%`,
            backgroundColor: 'var(--color-accent)',
            borderRadius: '5px',
            transition: 'width 0.3s ease'
          }}
        />
      </div>
    </div>
  );
};

const LocationWizard = () => {
  const navigate = useNavigate();
  const { brandId } = useParams();
  const { currentBrand } = useBrandContext();
  const [currentStep, setCurrentStep] = useState<LocationWizardStep>(LocationWizardSteps.BASIC_INFO);
  const [basicInfo, setBasicInfo] = useState<BasicInfoData>(initialBasicInfo);
  const [hours, setHours] = useState<HoursData>(initialHours);
  const [contact, setContact] = useState<ContactData>(initialContact);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toMilitaryTime = (time: string): string => {
    const match = time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return time;
    let hour = parseInt(match[1], 10);
    const minute = match[2];
    const period = match[3].toUpperCase();
    if (period === 'PM' && hour !== 12) hour += 12;
    if (period === 'AM' && hour === 12) hour = 0;
    return `${hour.toString().padStart(2, '0')}:${minute}`;
  };

  const handleCancel = useCallback(() => {
    navigate(`/${brandId}/${PAGE_LOCATIONS}`);
  }, [brandId, navigate]);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // Build hours in the format the API expects
      const restaurantHours = hours.entries
        .filter((entry) => entry.days.some(Boolean) && entry.startTime && entry.endTime)
        .map((entry) => ({
          day: entry.days
            .map((selected, index) => (selected ? DAY_MAP[index] : null))
            .filter((day): day is string => day !== null),
          start: toMilitaryTime(entry.startTime),
          end: toMilitaryTime(entry.endTime)
        }));

      // Create the restaurant
      const createdRestaurant = await createRestaurant({
        name: basicInfo.restaurantName,
        phone: basicInfo.phone,
        email: basicInfo.email,
        cuisineID: currentBrand?.cuisineID || 1,
        address: {
          address1: basicInfo.streetAddress1,
          address2: basicInfo.streetAddress2 || undefined,
          city: basicInfo.city,
          governingDistrict: basicInfo.state,
          postalCode: basicInfo.zip,
          country: 'United States'
        },
        socials: {
          facebook: '',
          instagram: '',
          snapchat: '',
          tiktok: '',
          twitter: ''
        },
        restaurantHours
      });

      // Assign to the current brand
      if (currentBrand?.id && createdRestaurant?.restaurantID) {
        await assignRestaurantToBrand(currentBrand.id, createdRestaurant.restaurantID);
      }

      // TODO: Save contact when contact endpoints are available
      // if (contact.firstName || contact.lastName) {
      // }

      toast.success('Location created successfully!', {
        position: toast.POSITION.TOP_RIGHT
      });

      navigate(`/${brandId}/${PAGE_LOCATIONS}`);
    } catch (error: unknown) {
      console.error('Error creating location:', error);

      const responseData = (
        error as {
          response?: {
            data?: {
              message?: string;
              error?: string;
              errors?: { message?: string }[];
            };
          };
        }
      ).response?.data;

      const errorMessage =
        responseData?.errors
          ?.map((item) => item.message?.trim())
          .filter((message): message is string => Boolean(message))
          .map((message) => message.charAt(0).toUpperCase() + message.slice(1))
          .join('. ') ||
        responseData?.message ||
        responseData?.error ||
        'Failed to create location. Please try again.';

      toast.error(errorMessage, {
        position: toast.POSITION.TOP_RIGHT
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [basicInfo, hours, brandId, currentBrand, isSubmitting, navigate]);

  return (
    <div className="restaurant-wizard-container">
      <div className="restaurant-wizard-hero-background" />
      <div className="restaurant-wizard-scroll-body">
        <div className="restaurant-wizard-header">
          <div className="restaurant-wizard-header-badge">
            <img src={tapTabLogo} alt="" className="restaurant-wizard-header-logo" aria-hidden />
            <h1 className="restaurant-wizard-header-title" style={{ marginTop: '12px' }}>
              ADD LOCATION
            </h1>
          </div>
        </div>

        <ProgressBar currentStep={currentStep} />

        <div className="restaurant-wizard-content">
          {currentStep === LocationWizardSteps.BASIC_INFO && (
            <LocationBasicInfoStep
              data={basicInfo}
              onChange={setBasicInfo}
              onNext={() => setCurrentStep(LocationWizardSteps.HOURS)}
              onCancel={handleCancel}
            />
          )}
          {currentStep === LocationWizardSteps.HOURS && (
            <LocationHoursStep
              data={hours}
              onChange={setHours}
              onNext={() => setCurrentStep(LocationWizardSteps.CONTACT)}
              onBack={() => setCurrentStep(LocationWizardSteps.BASIC_INFO)}
            />
          )}
          {currentStep === LocationWizardSteps.CONTACT && (
            <LocationContactStep
              data={contact}
              onChange={setContact}
              onSubmit={handleSubmit}
              onBack={() => setCurrentStep(LocationWizardSteps.HOURS)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationWizard;
